import { loadSquareWebSdk } from '@/services/squareWebSdk';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, Lock } from 'lucide-react';
import axios from 'axios';

/**
 * Public statement-payment page.
 *
 * Loads its own balance via the magic-link token in the URL — no login.
 * Renders the Square Web Payments SDK card form, takes a partial or
 * full payment, and shows confirmation.
 *
 * Why a custom page (not part of AppShell):
 *   - No auth required; AppShell forces a login redirect
 *   - Different layout — single-purpose, branded for the practice, not
 *     the staff-facing UI
 *   - Square Web Payments SDK loads from an external script, so we
 *     keep that load contained to this page
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function PayLinkPage() {
  const { token } = useParams();
  return <PaymentSession key={token} token={token} />;
}

function PaymentSession({ token }) {
  const submitLock = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const pendingPayment = useRef(null);
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [card, setCard] = useState(null);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [practiceReview, setPracticeReview] = useState(false);
  const cardContainerRef = useRef(null);

  // Fetch the balance + Square config
  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE}/pay-link/${token}`)
      .then((r) => {
        if (!active) return;
        if (!r.data?.patient || !r.data?.tenant || typeof r.data.balance !== 'number' || !Number.isFinite(r.data.balance) ||
            (r.data.items && (!Array.isArray(r.data.items) || r.data.items.some(item => typeof item.balance !== 'number' || !Number.isFinite(item.balance)))))
          throw new Error('Invalid payment balance response');
        setState({ loading: false, data: r.data, error: null });
        if (r.data.balance > 0) setAmount(r.data.balance.toFixed(2));
      })
      .catch((err) => {
        if (!active) return;
        const code = err.response?.status;
        const msg = code === 410 ? 'This payment link has expired.'
          : code === 404 ? 'Payment link not found.'
          : 'Could not load this payment link.';
        setState({ loading: false, data: null, error: msg });
      });
    return () => { active = false; };
  }, [token]);

  // Initialize Square Web Payments SDK once we have the data and the DOM is ready
  useEffect(() => {
    if (paymentResult || !state.data || !state.data.square_application_id || state.data.paid || state.data.review_required || state.data.collection_pending) return;
    if (typeof window === 'undefined') return;

    let mounted = true, createdCard = null;
    async function init() {
      try {
        const square = await loadSquareWebSdk(state.data.square_environment);
        if (!mounted) return;
        const payments = square.payments(
          state.data.square_application_id,
          state.data.square_location_id,
        );
        const c = await payments.card();
        createdCard = c;
        if (!mounted) { await c.destroy(); return; }
        await c.attach(cardContainerRef.current);
        if (mounted) setCard(c);
        else await c.destroy();
      } catch (err) {
        if (!mounted) return;
        console.error('Square SDK init failed');
        setState((s) => ({ ...s, error: err.message || 'Card form failed to load. Please try again or call our office.' }));
      }
    }
    init();
    return () => { mounted = false; if (createdCard) Promise.resolve().then(() => createdCard.destroy()).catch(() => {}); };
  }, [state.data, paymentResult]);

  async function handlePay(e) {
    e.preventDefault();
    if (!card || state.data?.review_required || state.data?.collection_pending || submitLock.current || paymentResult || practiceReview) return;

    const amt = pendingPayment.current?.amount ?? Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setPaymentError('Enter a valid amount'); return; }
    if (amt > state.data.balance + 0.01) { setPaymentError(`Amount exceeds balance ($${state.data.balance.toFixed(2)})`); return; }

    submitLock.current = true;
    setSubmitting(true);
    setPaymentError('');
    try {
      const tokenResult = pendingPayment.current ? { status: 'OK', token: pendingPayment.current.source_id } : await card.tokenize();
      if (!alive.current) return;
      if (tokenResult.status !== 'OK') {
        setPaymentError(tokenResult.errors?.[0]?.message || 'Card tokenization failed');
        return;
      }
      pendingPayment.current ||= { source_id: tokenResult.token, amount: amt };
      const r = await axios.post(`${API_BASE}/pay-link/${token}/pay`, pendingPayment.current);
      if (r.data?.success !== true || !r.data.payment_id ||
          typeof r.data.amount_paid !== 'number' || !Number.isFinite(r.data.amount_paid) || r.data.amount_paid <= 0 ||
          typeof r.data.remaining_balance !== 'number' || !Number.isFinite(r.data.remaining_balance) || r.data.remaining_balance < 0)
        throw new Error('Payment receipt was not confirmed');
      setPaymentResult(r.data);
    } catch (err) {
      const declined = err.response?.data?.error === 'card_declined';
      const refusal = [400, 409].includes(err.response?.status) &&
        err.response?.data?.error === 'payment_unconfirmed' &&
        typeof err.response.data.message === 'string' && !!err.response.data.message.trim();
      if (declined) pendingPayment.current = null;
      setUncertain(!!pendingPayment.current);
      // A collection hold may coexist with an earlier unconfirmed payment.
      // Preserve the operation; never describe this as a decline or clear it
      // for fresh tokenization. The practice must resolve the specific hold.
      setPracticeReview(refusal);
      setPaymentError(refusal
        ? `${err.response.data.message} Please contact your practice before trying another payment.`
        : pendingPayment.current
        ? 'Payment is not yet confirmed. Retry this same payment below or contact your practice. Do not start a new payment or reload this page.'
        : err.response?.data?.message || 'Your card was not charged. Check the card details and try again.');
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full rounded-xl bg-white p-8 shadow-sm text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-3 text-[18px] font-bold text-gray-900">Payment link unavailable</h1>
          <p className="mt-1 text-[13px] text-gray-600">{state.error}</p>
          <p className="mt-4 text-[12px] text-gray-500">Please contact our office to make a payment.</p>
        </div>
      </div>
    );
  }

  if (paymentResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full rounded-xl bg-white p-8 shadow-sm text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h1 className="mt-3 text-[20px] font-bold text-gray-900">Thank you, {state.data.patient.first_name}!</h1>
          <p className="mt-1 text-[14px] text-gray-600">Your payment of <strong>${paymentResult.amount_paid.toFixed(2)}</strong> was received.</p>
          {paymentResult.card_last_4 && (
            <p className="mt-1 text-[12px] text-gray-500">Card ending {paymentResult.card_last_4}</p>
          )}
          {paymentResult.remaining_balance > 0 && (
            <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              Remaining balance: <strong>${paymentResult.remaining_balance.toFixed(2)}</strong>. You can return to this link anytime to pay more.
            </div>
          )}
          <p className="mt-5 text-[11px] text-gray-400">{state.data.tenant.name}</p>
        </div>
      </div>
    );
  }

  if (state.data.review_required || state.data.collection_pending) return <div className="mx-auto mt-16 max-w-md rounded-xl border bg-white p-6"><h1 className="text-xl font-semibold">Please contact your practice</h1><p className="mt-3">{state.data.collection_pending ? 'A previous payment is still being confirmed. Please contact your practice before starting another payment.' : 'Your account has credits that need review before another payment. Online collection is on hold.'}</p>{state.data.tenant.phone && <p className="mt-3">{state.data.tenant.phone}</p>}</div>;

  if (state.data.paid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full rounded-xl bg-white p-8 shadow-sm text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <h1 className="mt-3 text-[18px] font-bold text-gray-900">All caught up!</h1>
          <p className="mt-1 text-[13px] text-gray-600">Your account has a zero balance. Thank you, {state.data.patient.first_name}.</p>
          <p className="mt-4 text-[11px] text-gray-400">{state.data.tenant.name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        {/* Practice header */}
        <div className="text-center mb-6">
          <h1 className="text-[20px] font-bold text-gray-900">{state.data.tenant.name}</h1>
          {state.data.tenant.phone && <p className="text-[12px] text-gray-500">{state.data.tenant.phone}</p>}
        </div>

        {/* Balance card */}
        <div className="rounded-xl bg-white p-6 shadow-sm mb-4">
          <p className="text-[13px] text-gray-600">Hi {state.data.patient.first_name},</p>
          <p className="mt-1 text-[13px] text-gray-600">Your current account balance:</p>
          <div className="mt-2 text-[36px] font-bold text-gray-900">${state.data.balance.toFixed(2)}</div>

          {state.data.items?.length > 0 && (
            <details className="mt-4 text-[12px]">
              <summary className="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
                View {state.data.items.length} line item{state.data.items.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 space-y-1.5 text-gray-700">
                {state.data.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between border-b border-gray-100 pb-1">
                    <span>{String(i.date_of_service).slice(0, 10)} — {i.description}</span>
                    <strong>${i.balance.toFixed(2)}</strong>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Payment form */}
        <form onSubmit={handlePay} className="rounded-xl bg-white p-6 shadow-sm">
          <div className="mb-4">
            <label htmlFor="pay-link-amount" className="block text-[12px] font-bold text-gray-700 mb-1">Payment amount</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-[16px]">$</span>
              <input id="pay-link-amount" disabled={submitting || uncertain} type="number" step="0.01" min="0.01" max={state.data.balance + 0.01} value={amount}
                onChange={(e) => setAmount(e.target.value)} required
                className="flex-1 rounded border border-gray-300 px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="mt-1.5 flex gap-2 text-[11px]">
              <button type="button" disabled={submitting || uncertain} onClick={() => setAmount(state.data.balance.toFixed(2))}
                className="text-primary-600 hover:underline">Pay full balance</button>
              <span className="text-gray-300">|</span>
              <button type="button" disabled={submitting || uncertain} onClick={() => setAmount((state.data.balance / 2).toFixed(2))}
                className="text-primary-600 hover:underline">Pay half</button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-[12px] font-bold text-gray-700 mb-1">Card details</label>
            <div id="sq-card-container" ref={cardContainerRef} className="rounded border border-gray-300 px-3 py-2 min-h-[60px] bg-white" />
            {!state.data.square_application_id && (
              <p className="mt-1 text-[11px] text-amber-700">Payment processing is not configured. Please call to pay.</p>
            )}
          </div>

          {paymentError && <p role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{paymentError}</p>}
          {practiceReview && state.data.tenant.phone && <p className="mb-4 text-sm text-gray-700">Call your practice: {state.data.tenant.phone}</p>}
          <button type="submit" disabled={!card || submitting || practiceReview}
            className="w-full rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 text-[14px] disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {submitting ? 'Processing…' : practiceReview ? 'Contact practice to continue' : `${uncertain ? 'Retry same payment' : 'Pay'} $${Number(amount || '0').toFixed(2)}`}
          </button>
          <p className="mt-3 text-center text-[10px] text-gray-400 flex items-center justify-center gap-1">
            <Lock className="h-3 w-3" /> Secured by Square. Your card information never touches our servers.
          </p>
        </form>
      </div>
    </div>
  );
}
